import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { useAuth } from '../../../hooks/useAuth';
import { getAuth } from 'firebase/auth';
import { GetAllAnalyses } from '../../../Client/ManagementServiceClient';
import './AnalysisHistory.css';

const AnalysisHistory = () => {
  const [analyses, setAnalyses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!user) {
      setError('You must be logged in to view analysis history');
      setLoading(false);
      return;
    }

    const fetchAnalyses = async () => {
      try {
        setLoading(true);
        const auth = getAuth();
        const idToken = await auth.currentUser.getIdToken();
        
        const result = await GetAllAnalyses(idToken);
        
        if (result.status === 200 && result.myJson?.analysis_results) {
          setAnalyses(result.myJson.analysis_results || []);
        } else if (result.status === 200 && Array.isArray(result.myJson)) {
          setAnalyses(result.myJson);
        } else {
          setError('Failed to load analyses');
        }
      } catch (err) {
        setError('Error loading analysis history: ' + (err.message || 'Unknown error'));
        console.error('Error fetching analyses:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchAnalyses();
  }, [user]);

  const getFilteredAndSorted = () => {
    const sorted = [...analyses].sort((a, b) => {
      return new Date(b.analysis_timestamp) - new Date(a.analysis_timestamp);
    });
    return sorted;
  };

  const handleViewAnalysis = (analysisId) => {
    navigate(`/AnalysisDetails/${analysisId}`);
  };

  const handleDeleteAnalysis = async (analysisId, analysisIndex) => {
    if (!window.confirm('Are you sure you want to delete this analysis? This action cannot be undone.')) {
      return;
    }

    try {
      setDeleting(analysisId);
      const auth = getAuth();
      const idToken = await auth.currentUser.getIdToken();
      
      const { DeleteAnalysis } = await import('../../../Client/ManagementServiceClient');
      const result = await DeleteAnalysis(idToken, analysisId);
      
      if (result.status === 200 || result.status === 204) {
        // Remove from local state
        setAnalyses(analyses.filter((_, idx) => idx !== analysisIndex));
      } else {
        setError('Failed to delete analysis: ' + (result.myJson?.detail || 'Unknown error'));
      }
    } catch (err) {
      setError('Error deleting analysis: ' + (err.message || 'Unknown error'));
      console.error('Error deleting analysis:', err);
    } finally {
      setDeleting(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return 'Unknown';
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  const formatFileName = (fileName) => {
    return fileName?.length > 30 ? fileName.substring(0, 27) + '...' : fileName;
  };

  const parseJsonFromString = (value) => {
    if (!value) return null;
    if (typeof value === 'object') return value;
    if (typeof value !== 'string') return null;

    let text = value.trim();
    const fenceMatch = text.match(/```(?:json)?\s*([\s\S]*?)```/i);
    if (fenceMatch && fenceMatch[1]) {
      text = fenceMatch[1].trim();
    }

    try {
      return JSON.parse(text);
    } catch {
      return null;
    }
  };

  const averageNumbers = (...values) => {
    const valid = values.filter((v) => typeof v === 'number' && !Number.isNaN(v));
    if (!valid.length) return null;
    return valid.reduce((sum, v) => sum + v, 0) / valid.length;
  };

  const getDerivedMetrics = (analysis) => {
    const result = analysis.analysis_results || {};
    const analysisNode = result.analysis || {};
    const clarity = parseJsonFromString(
      analysisNode.clarity_engagement || result.clarity_engagement
    );
    const claims = parseJsonFromString(
      analysisNode.claims_risk || result.claims_risk
    );

    const avgScore = averageNumbers(
      clarity?.scores?.hook_strength,
      clarity?.scores?.content_clarity,
      clarity?.scores?.pacing_quality
    );

    const qualityScore = result.content_analysis?.overall_score ?? avgScore;
    const predictedRetention = result.retention_analysis?.predicted_retention_rate ??
      (avgScore !== null ? avgScore * 10 : null);
    const issuesFound = result.issue_analysis?.issues?.length ?? claims?.risky_claims?.length ?? 0;
    const riskLevel = result.issue_analysis?.overall_risk_level ?? claims?.overall_risk_level ?? 'Unknown';
    const timestamp = analysis.analysis_timestamp || result.analysis_timestamp || analysis.created_at;

    return {
      result,
      qualityScore,
      predictedRetention,
      issuesFound,
      riskLevel,
      timestamp
    };
  };

  if (loading) {
    return (
      <div className="analysis-history-container">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading your analysis history...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="analysis-history-container">
        <div className="error-state">
          <p>{error}</p>
        </div>
      </div>
    );
  }

  const filteredAnalyses = getFilteredAndSorted();

  return (
    <div className="analysis-history-container">

      <div className="history-header">
        <h1>Analyze</h1>
        <button className="btn-primary" onClick={() => navigate('/VideoAnalysis')}>
          + New Analysis
        </button>
      </div>

      {filteredAnalyses.length === 0 ? (
        <div className="empty-state">
          <p>No analyses found. Start by creating a new analysis!</p>
          <button className="btn-secondary" onClick={() => navigate('/VideoAnalysis')}>
            Analyze a Video
          </button>
        </div>
      ) : (
        <>
          <div className="filters-section">
            <div className="results-count">
              Showing {filteredAnalyses.length} analysis result{filteredAnalyses.length !== 1 ? 's' : ''}
            </div>
          </div>

          <div className="analyses-list">
            {filteredAnalyses.map((analysis) => {
              const derived = getDerivedMetrics(analysis);
              const { result } = derived;
              const metadataEnvelope = parseJsonFromString(result.metadata) || result.metadata || {};
              const metadata = metadataEnvelope?.metadata || {};
              const title = metadata.title ||
                analysis.video_filename ||
                (result.transcript ? formatFileName(result.transcript) : 'Video Analysis Results');
              const subtitle = metadata.summary_headline ||
                (result.transcript ? formatFileName(result.transcript) : '');

              return (
              <div key={analysis.id || analysis._id} className="analysis-card">
                <div className="card-header">
                  <div className="header-title">
                    <h3>{title}</h3>
                    <p className="header-subtitle">{subtitle}</p>
                  </div>
                  <span className="provider-badge">AI</span>
                </div>

                <div className="card-body">
                  {metadata.description && (
                    <div className="description-box">
                      <p>{metadata.description}</p>
                    </div>
                  )}

                  {metadata.tags && metadata.tags.length > 0 && (
                    <div className="tags-container">
                      {metadata.tags.map((tag, idx) => (
                        <span key={idx} className="tag">{tag}</span>
                      ))}
                    </div>
                  )}

                  <div className="info-row">
                    <span className="label">Content Score:</span>
                    <span className="value score">
                      {(derived.qualityScore ?? 0).toFixed(1)}/10
                    </span>
                  </div>

                  <div className="info-row">
                    <span className="label">Risk Level:</span>
                    <span className={`value risk ${(derived.riskLevel || 'unknown')}`}>
                      {(derived.riskLevel || 'Unknown').toUpperCase()}
                    </span>
                  </div>

                  <div className="info-row">
                    <span className="label">Predicted Retention:</span>
                    <span className="value">
                      {(derived.predictedRetention ?? 0).toFixed(0)}%
                    </span>
                  </div>

                  <div className="info-row">
                    <span className="label">Analyzed:</span>
                    <span className="value">{formatDate(derived.timestamp)}</span>
                  </div>
                </div>

                <div className="card-footer">
                  <button 
                    className="btn-view"
                    onClick={() => handleViewAnalysis(analysis.id || analysis._id)}
                  >
                    View Details <ArrowRight size={14} aria-hidden="true" />
                  </button>
                  <button 
                    className="btn-delete"
                    onClick={() => handleDeleteAnalysis(analysis.id || analysis._id, filteredAnalyses.indexOf(analysis))}
                    disabled={deleting === (analysis.id || analysis._id)}
                    title="Delete this analysis"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="3 6 5 6 21 6"></polyline>
                      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                      <line x1="10" y1="11" x2="10" y2="17"></line>
                      <line x1="14" y1="11" x2="14" y2="17"></line>
                    </svg>
                  </button>
                </div>
              </div>
            );
            })}
          </div>
        </>
      )}
    </div>
  );
};

export default AnalysisHistory;
